import { api } from '@ui/app/api';
import { logger } from '@ui/utils/logger';
import type {
    ClinicalTrialJob,
    ClinicalTrialJobListResponse,
    CreateJobRequest,
    UpdateCriterionRequest,
    BulkUpdateCriteriaRequest,
    CreateCohortResponse,
    CriterionPatientsResponse,
    JobProgressEvent,
    JobStatus,
    CriteriaMatchResponse,
    CalculateCountResponse,
    QueryConfig,
} from '../types';

export const clinicalTrialApi = api.injectEndpoints({
    endpoints: builder => ({
        /**
         * Get all clinical trial jobs for the current user
         */
        getClinicalTrialJobs: builder.query<ClinicalTrialJobListResponse, void>({
            query: () => '/clinical-trial/jobs',
            providesTags: ['ClinicalTrialJobs'],
        }),

        /**
         * Get a single clinical trial job by ID with real-time SSE updates
         */
        getClinicalTrialJob: builder.query<ClinicalTrialJob, string>({
            query: id => `/clinical-trial/jobs/${id}`,
            providesTags: (result, error, id) => [{ type: 'ClinicalTrialJobs', id }],

            async onCacheEntryAdded(
                jobId,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState, dispatch }
            ) {
                await cacheDataLoaded;

                // Check if job needs SSE (is in progress or pending)
                const state = getState() as any;
                const cachedData = state.api?.queries?.[`getClinicalTrialJob("${jobId}")`]?.data as
                    | ClinicalTrialJob
                    | undefined;
                const status = cachedData?.status;

                // Only start SSE for jobs that may receive updates
                // States that need SSE: pending (waiting for parsing), parsing, executing
                // States that don't need SSE: parsed (user reviewing), completed, failed
                const needsSSE = status === 'pending' || status === 'parsing' || status === 'executing';
                if (!needsSSE) {
                    return;
                }

                const { fetchEventSource } = await import('@microsoft/fetch-event-source');
                const ctrl = new AbortController();
                const url = `/api/clinical-trial/jobs/${jobId}/progress/stream`;

                // Track previous status to detect transitions
                let previousStatus: JobStatus = status;

                try {
                    await fetchEventSource(url, {
                        signal: ctrl.signal,
                        credentials: 'include',
                        onopen: async response => {
                            if (!response.ok && response.status >= 400 && response.status < 500) {
                                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }
                        },
                        onmessage: event => {
                            try {
                                if (!event.data || event.data.trim() === '') {
                                    return;
                                }

                                const data = JSON.parse(event.data) as JobProgressEvent;
                                const newStatus = data.status;

                                // Check if status transitioned to a state that requires refetch
                                // - parsing → executing: auto-execution started, criteria now exist
                                // - any → parsed/completed/failed: terminal states with final data
                                const needsRefetch =
                                    previousStatus !== newStatus &&
                                    (newStatus === 'executing' ||
                                        newStatus === 'parsed' ||
                                        newStatus === 'completed' ||
                                        newStatus === 'failed');

                                if (needsRefetch) {
                                    // Invalidate cache to trigger refetch with full criteria data
                                    dispatch(
                                        clinicalTrialApi.util.invalidateTags([{ type: 'ClinicalTrialJobs', id: jobId }])
                                    );
                                    previousStatus = newStatus;
                                    return; // Skip cache update since we're refetching
                                }

                                previousStatus = newStatus || previousStatus;

                                switch (event.event) {
                                    case 'connected':
                                    case 'heartbeat':
                                        // Ignore connection and heartbeat events
                                        break;

                                    case 'initial':
                                        // Initial state from server - update cached data
                                        updateCachedData(draft => {
                                            if (data.status) draft.status = data.status;
                                            if (data.progress !== undefined) draft.progress = data.progress;
                                        });
                                        break;

                                    case 'progress':
                                        // Progress update
                                        updateCachedData(draft => {
                                            if (data.status) draft.status = data.status;
                                            if (data.progress !== undefined) draft.progress = data.progress;
                                            if (data.message) draft.current_step = data.message;

                                            // Update criterion if provided
                                            if (data.criterionId && draft.criteria) {
                                                const criterion = draft.criteria.find(
                                                    c => c.criterion_id === data.criterionId
                                                );
                                                if (criterion) {
                                                    // Check if criterion completed or is just starting
                                                    if (data.criterionCompleted) {
                                                        criterion.execution_status = data.success
                                                            ? 'completed'
                                                            : 'failed';
                                                        if (data.patientCount !== undefined) {
                                                            criterion.patient_count = data.patientCount;
                                                        }
                                                        if (data.sqlQuery) {
                                                            criterion.sql_query = data.sqlQuery;
                                                        }
                                                    } else {
                                                        criterion.execution_status = 'executing';
                                                    }
                                                }
                                            }

                                            // Update final patient count if provided
                                            if (data.finalPatientCount !== undefined && draft.executions?.[0]) {
                                                draft.executions[0].final_patient_count = data.finalPatientCount;
                                            }
                                        });
                                        break;

                                    default:
                                        // Handle any status update
                                        updateCachedData(draft => {
                                            if (data.status) draft.status = data.status;
                                            if (data.progress !== undefined) draft.progress = data.progress;
                                        });
                                        break;
                                }
                            } catch (err) {
                                logger.error('Failed to parse SSE event', err);
                            }
                        },
                        onerror: err => {
                            logger.error('SSE error', err);
                            // Don't throw - let the connection retry
                        },
                    });
                } catch (err: unknown) {
                    if (err instanceof Error && err.name !== 'AbortError') {
                        logger.error('SSE connection failed', err);
                    }
                }

                // Cleanup on cache entry removal
                await cacheEntryRemoved;
                ctrl.abort();
            },
        }),

        /**
         * Create a new clinical trial job
         */
        createClinicalTrialJob: builder.mutation<ClinicalTrialJob, CreateJobRequest>({
            query: body => ({
                url: '/clinical-trial/jobs',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['ClinicalTrialJobs'],
        }),

        /**
         * Delete a clinical trial job
         */
        deleteClinicalTrialJob: builder.mutation<void, string>({
            query: id => ({
                url: `/clinical-trial/jobs/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ClinicalTrialJobs'],
        }),

        /**
         * Start eligibility parsing for a job
         */
        startParsing: builder.mutation<{ message: string }, string>({
            query: jobId => ({
                url: `/clinical-trial/jobs/${jobId}/parse`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, jobId) => [{ type: 'ClinicalTrialJobs', id: jobId }],
        }),

        /**
         * Start query execution for a job
         */
        startExecution: builder.mutation<{ message: string }, string>({
            query: jobId => ({
                url: `/clinical-trial/jobs/${jobId}/execute`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, jobId) => [{ type: 'ClinicalTrialJobs', id: jobId }],
        }),

        /**
         * Retry failed criteria for a job
         */
        retryFailedCriteria: builder.mutation<
            { success: boolean; message: string; retrying_count: number },
            { jobId: string; criterionIds?: string[] }
        >({
            query: ({ jobId, criterionIds }) => ({
                url: `/clinical-trial/jobs/${jobId}/retry-failed`,
                method: 'POST',
                body: { criterion_ids: criterionIds },
            }),
            invalidatesTags: (result, error, { jobId }) => [{ type: 'ClinicalTrialJobs', id: jobId }],
        }),

        /**
         * Update a criterion's selection state
         */
        updateCriterion: builder.mutation<
            { success: boolean },
            { jobId: string; criterionId: string; data: UpdateCriterionRequest }
        >({
            query: ({ jobId, criterionId, data }) => ({
                url: `/clinical-trial/jobs/${jobId}/criteria/${criterionId}`,
                method: 'PUT',
                body: data,
            }),
            // Optimistic update
            async onQueryStarted({ jobId, criterionId, data }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    clinicalTrialApi.util.updateQueryData('getClinicalTrialJob', jobId, draft => {
                        const criterion = draft.criteria?.find(c => c.id === criterionId);
                        if (criterion) {
                            criterion.is_selected = data.is_selected;
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        /**
         * Bulk update criteria selection
         */
        bulkUpdateCriteria: builder.mutation<
            { success: boolean; updated: number },
            { jobId: string; data: BulkUpdateCriteriaRequest }
        >({
            query: ({ jobId, data }) => ({
                url: `/clinical-trial/jobs/${jobId}/criteria/bulk`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error, { jobId }) => [{ type: 'ClinicalTrialJobs', id: jobId }],
        }),

        /**
         * Create cohort from job results
         */
        createCohortFromJob: builder.mutation<CreateCohortResponse, string>({
            query: jobId => ({
                url: `/clinical-trial/jobs/${jobId}/create-cohort`,
                method: 'POST',
            }),
        }),

        /**
         * Get patients for a specific criterion
         */
        getCriterionPatients: builder.query<
            CriterionPatientsResponse,
            { jobId: string; criterionId: string; page?: number; pageSize?: number }
        >({
            query: ({ jobId, criterionId, page = 1, pageSize = 10 }) =>
                `/clinical-trial/jobs/${jobId}/criteria/${criterionId}/patients?page=${page}&page_size=${pageSize}`,
        }),

        /**
         * Get patients for combined selected criteria
         */
        getCombinedPatients: builder.query<
            CriterionPatientsResponse,
            { jobId: string; page?: number; pageSize?: number }
        >({
            query: ({ jobId, page = 1, pageSize = 10 }) =>
                `/clinical-trial/jobs/${jobId}/combined-patients?page=${page}&page_size=${pageSize}`,
            providesTags: (result, error, { jobId }) => [{ type: 'ClinicalTrialCombinedPatients' as const, id: jobId }],
        }),

        /**
         * Get criteria matches for a specific patient
         */
        getPatientCriteriaMatches: builder.query<CriteriaMatchResponse[], { jobId: string; personId: string }>({
            query: ({ jobId, personId }) => `/clinical-trial/jobs/${jobId}/patients/${personId}/criteria-matches`,
            keepUnusedDataFor: 300, // Cache in RTK for 5 minutes
        }),

        /**
         * Calculate patient count for selected criteria
         */
        calculateSelectedCount: builder.mutation<CalculateCountResponse, { jobId: string; criteriaIds: string[] }>({
            query: ({ jobId, criteriaIds }) => ({
                url: `/clinical-trial/jobs/${jobId}/calculate-count`,
                method: 'POST',
                body: { criteriaIds },
            }),
            // Invalidate combined patients cache when count is recalculated
            invalidatesTags: (result, error, { jobId }) => [
                { type: 'ClinicalTrialCombinedPatients' as const, id: jobId },
            ],
        }),

        /**
         * Save query config (grouped criteria layout) to a job
         */
        saveQueryConfig: builder.mutation<{ success: boolean }, { jobId: string; queryConfig: QueryConfig }>({
            query: ({ jobId, queryConfig }) => ({
                url: `/clinical-trial/jobs/${jobId}/query-config`,
                method: 'PUT',
                body: { queryConfig },
            }),
            invalidatesTags: (result, error, { jobId }) => [
                { type: 'ClinicalTrialJobs', id: jobId },
                { type: 'ClinicalTrialCombinedPatients' as const, id: jobId },
            ],
        }),

        /**
         * Calculate patient count using grouped query config
         */
        calculateGroupedCount: builder.mutation<CalculateCountResponse, { jobId: string; queryConfig: QueryConfig }>({
            query: ({ jobId, queryConfig }) => ({
                url: `/clinical-trial/jobs/${jobId}/calculate-grouped-count`,
                method: 'POST',
                body: { queryConfig },
            }),
        }),
    }),
});

export const {
    useGetClinicalTrialJobsQuery,
    useGetClinicalTrialJobQuery,
    useCreateClinicalTrialJobMutation,
    useDeleteClinicalTrialJobMutation,
    useStartParsingMutation,
    useStartExecutionMutation,
    useRetryFailedCriteriaMutation,
    useUpdateCriterionMutation,
    useBulkUpdateCriteriaMutation,
    useCreateCohortFromJobMutation,
    useGetCriterionPatientsQuery,
    useLazyGetCriterionPatientsQuery,
    useGetCombinedPatientsQuery,
    useLazyGetPatientCriteriaMatchesQuery,
    useCalculateSelectedCountMutation,
    useSaveQueryConfigMutation,
    useCalculateGroupedCountMutation,
} = clinicalTrialApi;
